/**
 * The demo's certificate register, kept the way the platform keeps it.
 *
 * Under the mock the certification boards still draw their own sample of
 * twenty-three certificates; what a person records there is carried here, so
 * the demo says "saved" exactly as the hosted site does, and refuses exactly
 * what the platform refuses: an issue with no expiry, an issue label the
 * certificate already has, a reference another certificate uses, a second
 * file on an issue that has one, an issue already in force.
 *
 * One leniency, and it is the demo's: the sample certificates are not library
 * documents (they live in the boards' tables, which the entry chunk must never
 * import), so a renewal of one the store has not met yet is taken as the
 * board's word that it exists. The platform never sees a sample id — its
 * boards show only what the library holds.
 *
 * The store starts empty and lives for the session; it never touches the
 * demo library (`mock/compliance.ts`), whose figures the Regulatory desk and
 * the tests count.
 */
import { DataSourceError } from "../api/dataSource";
import {
  CERTIFICATE_TYPE,
  type AttachedFile,
  type CertificateIssueInput,
  type CertificateWrite,
  type NewCertificateInput,
} from "../domain/certificates";
import type { RevisionStatus, RevisionTransitionResult } from "../domain/library";

interface Issue {
  id: string;
  revision: string;
  sequence: number;
  status: RevisionStatus;
  issuedOn?: string;
  expiresOn: string;
  changeNote?: string;
  attrs: Record<string, unknown>;
  file?: { name: string; size: number; type: string };
  at: string;
}

interface Held {
  id: string;
  type: string;
  title: string;
  reference?: string;
  attrs: Record<string, unknown>;
  issues: Issue[];
}

const held = new Map<string, Held>();
/* what a key was answered with, so a retried write is answered the same way */
const answered = new Map<string, unknown>();
let seq = 0;

const mint = (prefix: string): string => `${prefix}-${Date.now().toString(36)}-${(++seq).toString(36)}`;

function replay<T>(key: string | undefined, step: string, run: () => T): T {
  if (!key) return run();
  const k = `${key}:${step}`;
  if (answered.has(k)) return answered.get(k) as T;
  const out = run();
  answered.set(k, out);
  return out;
}

function addIssue(doc: Held, input: CertificateIssueInput, key?: string): CertificateWrite {
  return replay(key, "rev", () => {
    if (!input.expiresOn)
      throw new DataSourceError(
        `document type '${doc.type}' declares an expiry, so this revision needs an expires_on date — a certificate with no expiry is a certificate nobody will notice lapsing`,
        { status: 422 },
      );
    if (doc.issues.some((i) => i.revision === input.revision))
      throw new DataSourceError(
        `This document already has an issue labelled '${input.revision}'. Use the reference printed on the new one.`,
        { status: 409 },
      );
    const issue: Issue = {
      id: mint("rev"),
      revision: input.revision,
      sequence: doc.issues.length + 1,
      status: "draft",
      issuedOn: input.issuedOn,
      expiresOn: input.expiresOn,
      changeNote: input.changeNote,
      attrs: input.issuedBy ? { issued_by: input.issuedBy } : {},
      file: input.file ? { name: input.file.name, size: input.file.size, type: input.file.type } : undefined,
      at: new Date().toISOString(),
    };
    doc.issues.push(issue);
    const recorded: CertificateWrite = {
      documentId: doc.id,
      revisionId: issue.id,
      revision: issue.revision,
      status: "draft",
      issued: false,
    };
    if (!input.issueNow) return { ...recorded, waiting: "approver" as const };
    putInForce(doc, issue);
    return { ...recorded, status: "issued" as const, issued: true };
  });
}

function putInForce(doc: Held, issue: Issue): void {
  for (const other of doc.issues) if (other !== issue && other.status === "issued") other.status = "superseded";
  issue.status = "issued";
  issue.issuedOn = issue.issuedOn ?? new Date().toISOString().slice(0, 10);
}

export function mockRecordCertificate(input: NewCertificateInput, key?: string): CertificateWrite {
  const doc = replay(key, "doc", () => {
    const ref = input.reference?.trim().toLowerCase();
    if (ref && [...held.values()].some((d) => d.reference?.trim().toLowerCase() === ref))
      throw new DataSourceError(
        `Another document at this site already uses the reference '${input.reference}'. Check the reference, or leave it blank.`,
        { status: 409 },
      );
    const d: Held = {
      id: mint("cert"),
      type: input.type ?? CERTIFICATE_TYPE,
      title: input.title,
      reference: input.reference,
      attrs: { ...input.attrs },
      issues: [],
    };
    held.set(d.id, d);
    return d;
  });
  return addIssue(held.get(doc.id) ?? doc, input.issue, key);
}

export function mockRenewCertificate(documentId: string, input: CertificateIssueInput, key?: string): CertificateWrite {
  let doc = held.get(documentId);
  if (!doc) {
    /* a sample certificate the board shows: taken on its word (see the header) */
    doc = { id: documentId, type: CERTIFICATE_TYPE, title: documentId, attrs: {}, issues: [] };
    held.set(documentId, doc);
  }
  return addIssue(doc, input, key);
}

function findIssue(revisionId: string): { doc: Held; issue: Issue } {
  for (const doc of held.values()) {
    const issue = doc.issues.find((i) => i.id === revisionId);
    if (issue) return { doc, issue };
  }
  throw new DataSourceError("no such revision", { status: 404 });
}

export function mockIssueCertificate(revisionId: string): RevisionTransitionResult {
  const { doc, issue } = findIssue(revisionId);
  if (issue.status !== "draft" && issue.status !== "approved")
    throw new DataSourceError(`a revision that is ${issue.status} cannot become issued`, { status: 409 });
  const from = issue.status;
  putInForce(doc, issue);
  return { revisionId, from, to: "issued" };
}

export function mockAttachFile(revisionId: string, file: File, key?: string): AttachedFile {
  return replay(key, "file", () => {
    const { issue } = findIssue(revisionId);
    if (issue.file)
      throw new DataSourceError("This issue already has its file. To file a new certificate, record a new issue.", {
        status: 409,
      });
    issue.file = { name: file.name, size: file.size, type: file.type };
    return { revisionId, bytes: file.size, contentType: file.type || undefined };
  });
}

export function mockDownload(revisionId: string): Blob {
  const { doc, issue } = findIssue(revisionId);
  if (!issue.file) throw new DataSourceError("Not found: this revision has no file attached", { status: 404 });
  return new Blob(
    [`${doc.title}\n${issue.revision} — ${issue.file.name}\n(The demo keeps the record, not the file.)`],
    {
      type: "text/plain",
    },
  );
}

/** For tests: what the store holds. */
export const mockCertificatesHeld = (): ReadonlyMap<string, Held> => held;
